import React, { forwardRef, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const CustomTable = forwardRef(
  (
    {
      columns = [],
      data = [],
      onRowClick,
      renderRow,
      emptyText = "No Data to Display",
    },
    ref
  ) => {
    const tableRef = useRef(null);
    // console.log("Table data:", data);
    // console.log("Table columns:", columns);

    const onClickRow = (row) => {
      if (onRowClick) onRowClick(row);
    };

    // Separate columns into fixed left, scrollable, and fixed right
    const fixedLeftColumns = columns.filter((col) => col.fixed === "left");
    const fixedRightColumns = columns.filter((col) => col.fixed === "right");
    const scrollableColumns = columns.filter((col) => !col.fixed);

    // Calculate cumulative positions for fixed columns
    useEffect(() => {
      const updateStickyPositions = () => {
        if (!tableRef.current) return;

        const table = tableRef.current;

        // Calculate left positions for fixed left columns
        if (fixedLeftColumns.length > 0) {
          let cumulativeLeft = 0;

          fixedLeftColumns.forEach((col, index) => {
            const headerCells = table.querySelectorAll(`thead .stickyLeft-${index}`);
            const bodyCells = table.querySelectorAll(`tbody .stickyLeft-${index}`);
            
            // console.log(`Found ${headerCells.length} header cells and ${bodyCells.length} body cells for stickyLeft-${index}`);
            
            // Apply to both header and body cells - only set left position, let CSS handle sticky
            [...headerCells, ...bodyCells].forEach((cell) => {
              cell.style.left = `${cumulativeLeft}px`;
            });
            
            if (headerCells.length > 0) {
              cumulativeLeft += headerCells[0].offsetWidth;
            }
          });
        }

        // Calculate right positions for fixed right columns
        if (fixedRightColumns.length > 0) {
          let cumulativeRight = 0;

          for (let i = fixedRightColumns.length - 1; i >= 0; i--) {
            const headerCells = table.querySelectorAll(`thead .stickyRight-${i}`);
            const bodyCells = table.querySelectorAll(`tbody .stickyRight-${i}`);
            
            // Apply to both header and body cells - only set right position, let CSS handle sticky
            [...headerCells, ...bodyCells].forEach((cell) => {
              cell.style.right = `${cumulativeRight}px`;
            });
            
            if (headerCells.length > 0) {
              cumulativeRight += headerCells[0].offsetWidth;
            }
          }
        }
      };

      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(updateStickyPositions, 0);
      window.addEventListener('resize', updateStickyPositions);
      return () => window.removeEventListener('resize', updateStickyPositions);
    }, [fixedLeftColumns, fixedRightColumns, data]);

    // Render table section (header)
    const renderTableSection = (cols, sectionType, stickyPrefix = "") => {
      return cols.map((col, colIndex) => {
        const stickyClass = stickyPrefix ? `${stickyPrefix}-${colIndex}` : "";
        return (
          <th
            key={col.key || colIndex}
            className={`tableHead ${stickyClass}`}
            style={{ 
              width: col.width || "auto", 
              minWidth: col.width || "auto",
              textAlign: col.align || "left",
            }}
          >
            {col.title}
          </th>
        );
      });
    };

    // Render body cells for a specific column group
    const renderBodyCells = (cols, row, stickyPrefix = "") => {
      return cols.map((col, colIndex) => {
        const stickyClass = stickyPrefix ? `${stickyPrefix}-${colIndex}` : "";
        return (
          <td
            key={col.key || colIndex}
            className={`tableData ${stickyClass}`}
            style={{ 
              width: col.width || "auto", 
              minWidth: col.width || "auto",
              height: col.height || "40px",
              textAlign: col.align || "left",
            }}
          >
            {col.render ? col.render(row[col.key], row) : row[col.key]}
          </td>
        );
      });
    };

    return (
      <div className="tableContainer" ref={ref}>
        <div className="tableWrapper" ref={tableRef}>
          <table className="tableSec">
            <thead>
              <tr className="tableHeadRow" style={{ height: "50px" }}>
                {renderTableSection(fixedLeftColumns, "header", "stickyLeft")}
                {renderTableSection(scrollableColumns, "header")}
                {renderTableSection(fixedRightColumns, "header", "stickyRight")}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr className="tableBodyRow">
                  <td
                    colSpan={columns.length || 0}
                    className="tableBodyRowEmptyRow"
                  >
                    {emptyText}
                  </td>
                </tr>
              ) : (
                data.map((row, rowIndex) => {
                  if (renderRow) return renderRow(row, rowIndex, columns);
                  return (
                    <tr
                      key={rowIndex}
                      className="tableBodyRow"
                      onClick={() => onClickRow(row)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onClickRow(row);
                        }
                      }}
                    >
                      {renderBodyCells(fixedLeftColumns, row, "stickyLeft")}
                      {renderBodyCells(scrollableColumns, row)}
                      {renderBodyCells(fixedRightColumns, row, "stickyRight")}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

CustomTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      render: PropTypes.func,
      className: PropTypes.string,
      width: PropTypes.string,
      height: PropTypes.string,
      fixed: PropTypes.oneOf(["left", "right"]),
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowClick: PropTypes.func,
  renderRow: PropTypes.func,
  emptyText: PropTypes.string,
};

export default CustomTable;
